import React, { useEffect, useMemo, useState } from 'react';
import { IntersectionOptions, useInView } from 'react-intersection-observer';

import { useDocumentVisible } from './useDocumentVisible';

interface DocumentVisibleOnceProps {
  options?: IntersectionOptions;
  disableUntilScroll?: boolean;
}
export function useDocumentVisibleOnce({ options, disableUntilScroll = false }: DocumentVisibleOnceProps = {}) {
  const [isOnce, setIsOnce] = useState(false);
  const [isScroll, setIsScroll] = useState(!disableUntilScroll);
  const [isPermanentlyOnce, setIsPermanentlyOnce] = useState(false);
  const documentVisible = useDocumentVisible();
  const { ref, inView } = useInView(options);
  const shouldRefetch = useMemo(() => {
    if (!isScroll) return false;

    if (documentVisible && inView && !isOnce) {
      // 페이지 리포커싱 후 아이템 목록 영역에 위치해 있다면, 찜하기 refetch
      return true;
    }

    // 초기 init일 경우 1회 fetch
    if (isOnce) {
      return true;
    }

    return false;
  }, [isOnce, documentVisible, inView, isScroll]);

  useEffect(() => {
    if (!documentVisible) {
      setIsOnce(false);
    }
  }, [documentVisible]);

  useEffect(() => {
    setTimeout(() => {
      if (inView && !isOnce) {
        setIsOnce(true);
        setIsPermanentlyOnce(true);
      }
    });
  }, [inView, isOnce]);

  /**
   * 부분 네이티브의 경우 상단 ATF 영역이 앱인 관계로,
   * 실제 웹뷰 스크롤 영역은 뷰포트 내부로 인지하기 때문에 스크롤 이벤트로 초기 호출 제어
   */
  useEffect(() => {
    function handleScroll() {
      setIsScroll(true);
      document.removeEventListener('scroll', handleScroll);
    }

    if (!isScroll) {
      document.addEventListener('scroll', handleScroll);

      return () => {
        document.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isScroll]);

  return {
    isOnce,
    isPermanentlyOnce,
    inView,
    visibleDocument: documentVisible && inView,
    shouldRefetch,
    ref,
  };
}
